import React, { Dispatch, SetStateAction } from "react";
import {
  ShareSection,
  ShareSuccessContext,
} from "components/shareSection/ShareSection";
import {
  ShareOutcome,
  Permission,
  RefetchOptions,
  RemoveAccessData,
  ResourceType,
} from "components/shareSection/entities";
import { UserModelRole, SharedAccess } from "target_wasm";
import { Dialog } from "components/main/utils/Dialog";
import { useThemedLoading } from "components/hooks/useThemedLoading";
import { Callout } from "components/common/Callout";

export interface ManageAccessDialogProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  resourceType: ResourceType;
  sharedWithList: SharedAccess | undefined;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  canShare: boolean;
  canRevokeAccess: boolean;
  onShare: (
    emails: string[],
    roleOrPermission: UserModelRole | Permission | null,
  ) => Promise<ShareOutcome | void>;
  onRevokeAccess: (data: RemoveAccessData | null) => Promise<void>;
  onSuccess?: (context: ShareSuccessContext) => void;
  onError?: (error: string, status?: number) => void;
  defaultRole?: UserModelRole;
  defaultPermission?: Permission;
  onRefetchSharedList?: (options?: RefetchOptions) => Promise<void>;
  userContinuationToken?: string | null;
  modelName?: string;
  onResendInvitation?: (email: string) => Promise<void>;
  /// Extra content rendered between the callout and the access list — used by
  /// datasets to surface the "also share my tables" toggle.
  extraContent?: React.ReactNode;
  /// Keep the composer visible but block the Share action (e.g. a dataset/table
  /// awaiting a published release). Threaded to the share buttons; `canShare`
  /// still governs whether the composer shows at all.
  shareDisabled?: boolean;
  /// Suppress the built-in resource-type callout when `extraContent` supplies
  /// its own (e.g. the release notice + inline publish pill), so the dialog
  /// never shows two stacked disclaimers.
  suppressResourceCallout?: boolean;
}

export const ManageAccessDialog: React.FC<ManageAccessDialogProps> = (
  props,
) => {
  const loadingGif = useThemedLoading();

  return (
    <Dialog
      open={props.open}
      title="Manage Access"
      onClose={props.onClose}
      className="w-150! max-w-[90vw] overflow-visible"
      autoSpacing={false}
    >
      {props.sharedWithList === undefined ? (
        <div className="flex items-center justify-center py-12">
          <img src={loadingGif} className="w-10 h-10 object-contain" />
        </div>
      ) : (
        <div className="flex flex-col w-full overflow-visible">
          {!props.suppressResourceCallout &&
            props.resourceType === ResourceType.Model && (
              <Callout variant="info" className="mb-4">
                People you share with can see this model&apos;s tables and
                import them directly if they have access, or request it
                otherwise. Imports only ever serve released versions.
              </Callout>
            )}
          {!props.suppressResourceCallout &&
            props.resourceType === ResourceType.Table && (
              <Callout variant="info" className="mb-4">
                Importers only get this table&apos;s released versions, so it
                must have a published release before any data is shared.
              </Callout>
            )}
          {!props.suppressResourceCallout &&
            props.resourceType === ResourceType.Dataset && (
              <Callout variant="info" className="mb-4">
                People you share with see the tables in this dataset&apos;s
                releases. Tables they can&apos;t access appear locked.
              </Callout>
            )}
          {props.extraContent}
          <ShareSection
            sharedWithList={props.sharedWithList}
            setSharedWithList={props.setSharedWithList}
            entityId={props.entityId}
            resourceType={props.resourceType}
            canShare={props.canShare}
            shareDisabled={props.shareDisabled}
            canRevokeAccess={props.canRevokeAccess}
            onShare={props.onShare}
            onRevokeAccess={props.onRevokeAccess}
            onSuccess={props.onSuccess}
            onError={props.onError}
            setIsShare={props.onClose}
            defaultRole={props.defaultRole}
            defaultPermission={props.defaultPermission}
            onRefetchSharedList={props.onRefetchSharedList}
            userContinuationToken={props.userContinuationToken}
            modelName={props.modelName}
            onResendInvitation={props.onResendInvitation}
          />
        </div>
      )}
    </Dialog>
  );
};
